/**
 * ================================================================
 * CUSTOM HOOK: useGlobalErrorToast
 * ================================================================
 *
 * @hook             useGlobalErrorToast
 * @description      Global error toast handler for RTK Query errors
 * @category         Error Handling Hook
 *
 * @purpose
 * - Listens for global error toast events dispatched from RTK Query
 * - Shows toast notifications for API errors when enabled
 * - Provides centralized error toast management
 *
 * @sideEffects
 * - Adds/removes event listeners on mount/unmount
 * - Shows toast notifications based on error events
 *
 * @dependencies
 * - useToast hook for toast functionality
 * - Custom error toast events from baseQuery
 *
 * @usage
 * // In your root component or layout
 * // function App() {
 * //   useGlobalErrorToast();
 * //   return <div>Your app content</div>;
 * // }
 *
 * ================================================================
 */

import { useEffect } from 'react';

import { useToast } from '@/components/ui/toast';

interface ToastErrorEventDetail {
  title: string;
  description: string;
  variant: 'destructive' | 'success' | 'default';
  status: number;
  tagName?: string;
}

export function useGlobalErrorToast() {
  const { toast } = useToast();

  useEffect(() => {
    // If the ToastEventBridge is active, skip attaching this hook to avoid
    // duplicate listeners that can cause rapid replace cycles (blinking).
    const wBridge = window as unknown as { __AP_TOAST_BRIDGE_ACTIVE__?: boolean };
    if (wBridge.__AP_TOAST_BRIDGE_ACTIVE__) {
      return;
    }

    const w = window as unknown as {
      __AP_ERROR_TOAST_LISTENER_COUNT__?: number;
      __AP_ERROR_TOAST_HANDLER__?: (event: CustomEvent<ToastErrorEventDetail>) => void;
      __AP_ERROR_TOAST_UI_STATE__?: {
        lastShownTime: number;
        collectTimer?: number;
        pendingMessages: string[];
      };
    };

    // Initialize global counter
    if (typeof w.__AP_ERROR_TOAST_LISTENER_COUNT__ !== 'number') {
      w.__AP_ERROR_TOAST_LISTENER_COUNT__ = 0;
    }

    // ================================================================
    // ERROR TOAST EVENT HANDLER
    // ================================================================
    //
    // Purpose: Handle error toast events from RTK Query
    // Triggered: When API errors occur with showToast=true
    // Action: Display toast notification to user
    // ================================================================

    const DISPLAY_COOLDOWN_MS = 1500; // Match baseQuery cooldown
    const COLLECT_WINDOW_MS = 250; // Batch near-simultaneous events

    if (!w.__AP_ERROR_TOAST_UI_STATE__) {
      w.__AP_ERROR_TOAST_UI_STATE__ = {
        lastShownTime: 0,
        collectTimer: undefined,
        pendingMessages: [],
      };
    }

    const handleErrorToast = (event: CustomEvent<ToastErrorEventDetail>) => {
      const { title, description, variant } = event.detail;
      const state = w.__AP_ERROR_TOAST_UI_STATE__!;
      const now = Date.now();
      const withinCooldown = now - state.lastShownTime < DISPLAY_COOLDOWN_MS;

      // Accumulate messages for brief window
      if (state.pendingMessages.length < 3) {
        state.pendingMessages.push(description);
      }

      const flush = () => {
        const ts = Date.now();
        if (ts - state.lastShownTime < DISPLAY_COOLDOWN_MS) return;
        const combined =
          state.pendingMessages.length > 1
            ? `${state.pendingMessages[0]} (+${state.pendingMessages.length - 1} more)`
            : state.pendingMessages[0] || description;
        toast({ title, description: combined, variant });
        state.lastShownTime = ts;
        state.pendingMessages = [];
        state.collectTimer = undefined;
      };

      if (!withinCooldown) {
        if (state.collectTimer == null) {
          state.collectTimer = window.setTimeout(flush, COLLECT_WINDOW_MS);
        }
      } else {
        if (state.collectTimer == null) {
          const delay = DISPLAY_COOLDOWN_MS - (now - state.lastShownTime) + COLLECT_WINDOW_MS;
          state.collectTimer = window.setTimeout(flush, Math.max(delay, COLLECT_WINDOW_MS));
        }
      }
    };

    // Attach only on first subscriber
    let attachedByThisHook = false;
    w.__AP_ERROR_TOAST_LISTENER_COUNT__ = (w.__AP_ERROR_TOAST_LISTENER_COUNT__ ?? 0) + 1;
    if (w.__AP_ERROR_TOAST_LISTENER_COUNT__ === 1) {
      window.addEventListener('showErrorToast', handleErrorToast as EventListener);
      w.__AP_ERROR_TOAST_HANDLER__ = handleErrorToast;
      attachedByThisHook = true;
    }

    // Cleanup: remove only when last subscriber unmounts
    return () => {
      w.__AP_ERROR_TOAST_LISTENER_COUNT__ = Math.max(
        0,
        (w.__AP_ERROR_TOAST_LISTENER_COUNT__ ?? 1) - 1,
      );
      if (
        w.__AP_ERROR_TOAST_LISTENER_COUNT__ === 0 &&
        attachedByThisHook &&
        w.__AP_ERROR_TOAST_HANDLER__
      ) {
        window.removeEventListener(
          'showErrorToast',
          w.__AP_ERROR_TOAST_HANDLER__ as unknown as EventListener,
        );
        delete w.__AP_ERROR_TOAST_HANDLER__;
      }
    };
  }, [toast]);

  // This hook doesn't return anything - it just sets up the listener
}

/**
 * ================================================================
 * END OF HOOK: useGlobalErrorToast
 * ================================================================
 *
 * @exports          useGlobalErrorToast - Global error toast handler
 * @dependencies     React hooks, useToast hook
 * @usedBy           Root component or layout components
 *
 * @integration
 * - Works with: RTK Query baseQuery error handling
 * - Requires: Toast provider to be set up in app
 * - Events: Listens for 'showErrorToast' custom events
 *
 * ================================================================
 */
